// PRC-040 (boceto G5): reconocer el tipo de archivo por sus columnas y resumir
// las líneas descartadas, con informe descargable en Excel.
import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import * as XLSX from 'xlsx';
import { JourneyButton, JourneyCard, JourneyText } from './JourneyBasics';
import { J } from '../journeyTokens';
import { toSpanishNumber } from '../formatNumber';

export type DetectedFileType = 'transactions' | 'customers' | 'products';

const normalize = (value: unknown) => String(value ?? '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

const has = (headers: string[], words: string[]) => headers.some((h) => words.some((w) => h === w || h.split(' ').includes(w) || h.includes(w)));

const CUSTOMER = ['customer', 'cliente', 'client', 'customer id', 'id cliente', 'cod cliente'];
const PRODUCT = ['product', 'producto', 'sku', 'articulo', 'referencia', 'ref', 'item'];
const QUANTITY = ['quantity', 'cantidad', 'qty', 'unidades', 'uds'];
const PRICE = ['unit price', 'price', 'precio', 'pvp', 'importe'];
const DATE = ['date', 'fecha', 'transaction date'];
const COST = ['cost', 'coste', 'costo', 'standard cost'];

/** Tipo de archivo según su primera fila no vacía; null si no se reconoce. */
export async function detectFileType(file: File): Promise<DetectedFileType | null> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', sheetRows: 20 });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) return null;
  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, blankrows: false });
  const headerRow = rows.find((row) => Array.isArray(row) && row.filter((c) => String(c ?? '').trim()).length >= 2) ?? [];
  const headers = headerRow.map(normalize).filter(Boolean);
  const hasCustomer = has(headers, CUSTOMER);
  const hasProduct = has(headers, PRODUCT);
  if (has(headers, QUANTITY) && has(headers, PRICE) && (has(headers, DATE) || (hasCustomer && hasProduct))) return 'transactions';
  if (hasProduct && !hasCustomer && (has(headers, COST) || headers.length >= 2)) return 'products';
  if (hasCustomer && !hasProduct) return 'customers';
  return null;
}

export interface DiscardSummaryData {
  total: number;
  reasons: Array<{ reason: string; count: number; example_rows: number[] }>;
  rows: Array<{ row: number; reason: string }>;
  rows_truncated: boolean;
}

const nf = (n: number) => toSpanishNumber(n);

export const downloadDiscardReport = (data: DiscardSummaryData, fileName?: string | null) => {
  const sheet = XLSX.utils.aoa_to_sheet([['Fila', 'Motivo'], ...data.rows.map((r) => [r.row, r.reason])]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, 'Descartes');
  const base = (fileName || 'ventas').replace(/\.[^.]+$/, '');
  XLSX.writeFile(workbook, `descartes_${base}.xlsx`);
};

export const DiscardSummary: React.FC<{ data: DiscardSummaryData; fileName?: string | null }> = ({ data, fileName }) => {
  if (!data || data.total === 0) return null;
  return (
    <JourneyCard sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <JourneyText variant="h3" component="h2">{nf(data.total)} líneas descartadas</JourneyText>
          <JourneyText variant="small">El resto del archivo se ha cargado. Corrige estas líneas y vuelve a subirlo cuando quieras.</JourneyText>
        </Box>
        <JourneyButton kind="ghost" small onClick={() => downloadDiscardReport(data, fileName)}>Descargar informe (Excel)</JourneyButton>
      </Box>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ '& .MuiTableCell-root': { borderColor: J.border, color: J.body, fontVariantNumeric: 'tabular-nums' }, '& .MuiTableCell-head': { color: J.muted, fontWeight: 700, fontSize: 12.5 } }}>
          <TableHead>
            <TableRow>
              <TableCell>Fila</TableCell>
              <TableCell>Motivo</TableCell>
              <TableCell align="right">Líneas</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.reasons.map((r) => (
              <TableRow key={r.reason}>
                <TableCell>{r.example_rows.map(nf).join(', ')}{r.count > r.example_rows.length ? '…' : ''}</TableCell>
                <TableCell>{r.reason}</TableCell>
                <TableCell align="right">{nf(r.count)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      {data.rows_truncated && <JourneyText variant="small">El informe incluye las primeras {nf(data.rows.length)} líneas descartadas.</JourneyText>}
      <JourneyText variant="small">Si falta el coste, la línea se carga igual: cuenta para ventas y queda «sin coste» para el margen.</JourneyText>
    </JourneyCard>
  );
};
